import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const StatusDropdown = ({ 
  installation, 
  onStatusChange, 
  onPhotoUpload,
  size = 'default'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showUndoSnackbar, setShowUndoSnackbar] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [previousStatus, setPreviousStatus] = useState(null);
  const [undoTimer, setUndoTimer] = useState(null);
  const dropdownRef = useRef(null);

  const currentStatus = installation?.status?.toLowerCase();
  const hasPhotos = installation?.photos?.length > 0;

  // Status configuration
  const getStatusConfig = (status) => {
    switch (status?.toLowerCase()) {
      case 'planned':
        return {
          label: 'Planned',
          icon: 'Calendar',
          className: 'bg-blue-100 text-blue-800 border-blue-200',
          chipColor: 'bg-blue-100 text-blue-800'
        };
      case 'installed':
        return {
          label: 'Installed',
          icon: 'CheckCircle',
          className: 'bg-[#9BD979]/20 text-[#7BA428] border-[#9BD979]',
          chipColor: 'bg-[#9BD979]/20 text-[#7BA428]'
        };
      case 'verified':
        return {
          label: 'Verified',
          icon: 'Shield',
          className: 'bg-[#28A745]/20 text-[#28A745] border-[#28A745]',
          chipColor: 'bg-[#28A745]/20 text-[#28A745]'
        };
      default:
        return {
          label: 'Unknown',
          icon: 'HelpCircle',
          className: 'bg-gray-100 text-gray-800 border-gray-200',
          chipColor: 'bg-gray-100 text-gray-800'
        };
    }
  };

  // Get available status options based on current status and conditions
  const getAvailableOptions = () => {
    const options = ['planned', 'installed'];
    
    // Add verified only if there's at least one image
    if (hasPhotos) {
      options?.push('verified');
    }
    
    return options?.map(status => ({
      value: status,
      ...getStatusConfig(status),
      disabled: false
    }));
  };

  // Check if status is read-only (Verified)
  const isReadOnly = currentStatus === 'verified';

  // Handle dropdown toggle
  const toggleDropdown = () => {
    if (isReadOnly) return;
    setIsOpen(!isOpen);
  };

  // Handle status selection
  const handleStatusSelect = (newStatus) => {
    if (newStatus === currentStatus || isReadOnly) {
      setIsOpen(false);
      return;
    }

    // Store the change for confirmation
    setPendingStatus(newStatus);
    setIsOpen(false);
    setShowConfirmModal(true);
  };

  // Handle confirmation
  const handleConfirm = () => {
    if (!pendingStatus) return;

    setPreviousStatus(currentStatus);
    onStatusChange?.(installation?.id, pendingStatus);
    setShowConfirmModal(false);
    setPendingStatus(null);
    
    // Show undo snackbar for 15 seconds
    setShowUndoSnackbar(true);
    const timer = setTimeout(() => {
      setShowUndoSnackbar(false);
      setPreviousStatus(null);
    }, 15000);
    setUndoTimer(timer);
  };

  // Handle cancel confirmation
  const handleCancel = () => {
    setShowConfirmModal(false);
    setPendingStatus(null);
  };

  // Handle undo
  const handleUndo = () => {
    if (!previousStatus) return;

    onStatusChange?.(installation?.id, previousStatus, { isUndo: true });
    setShowUndoSnackbar(false);
    setPreviousStatus(null);
    
    if (undoTimer) {
      clearTimeout(undoTimer);
      setUndoTimer(null);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(event?.target)) {
        setIsOpen(false);
      }
    };

    document?.addEventListener('mousedown', handleClickOutside);
    return () => {
      document?.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (undoTimer) {
        clearTimeout(undoTimer);
      }
    };
  }, [undoTimer]);

  const currentConfig = getStatusConfig(currentStatus);
  const sizeClasses = size === 'sm' ? 'px-2 py-1 text-xs' : 'px-3 py-1.5 text-sm';
  const iconSize = size === 'sm' ? 12 : 14;

  return (
    <>
      {/* Status Dropdown */}
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={toggleDropdown}
          disabled={isReadOnly}
          className={`
            inline-flex items-center gap-1.5 font-medium rounded-full border ${sizeClasses}
            ${currentConfig?.className}
            ${isReadOnly 
              ? 'cursor-default' :'cursor-pointer hover:shadow-md transition-all duration-200'
            }
          `}
        >
          <Icon name={currentConfig?.icon} size={iconSize} />
          {currentConfig?.label}
          {isReadOnly ? (
            <Icon name="Lock" size={10} className="ml-1" />
          ) : (
            <Icon name="ChevronDown" size={12} className={`ml-1 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
          )}
        </button>

        {/* Dropdown Menu */}
        {isOpen && !isReadOnly && (
          <div className="absolute top-full left-0 mt-1 w-56 bg-white border border-border rounded-lg shadow-lg z-50">
            <div className="p-2">
              {getAvailableOptions()?.map((option) => (
                <button
                  key={option?.value}
                  onClick={() => handleStatusSelect(option?.value)}
                  disabled={option?.disabled}
                  className={`
                    w-full flex items-center gap-3 p-2 rounded-md text-left
                    hover:bg-muted/50 transition-colors duration-150
                    ${option?.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                    ${option?.value === currentStatus ? 'bg-muted/30' : ''}
                  `}
                >
                  {/* Status chip */}
                  <span className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded-full border ${option?.chipColor} border-current`}>
                    <Icon name={option?.icon} size={10} />
                    {option?.label}
                  </span>
                  
                  {option?.value === currentStatus && (
                    <Icon name="Check" size={14} className="ml-auto text-primary" />
                  )}
                </button>
              ))}
              
              {/* Helper text when Verified is not available */}
              {!hasPhotos && (
                <div className="mt-2 p-2 bg-orange-50 border border-orange-200 rounded-md">
                  <div className="flex items-start gap-2">
                    <Icon name="Info" size={14} className="text-orange-600 mt-0.5 flex-shrink-0" />
                    <div className="text-xs text-orange-800">
                      <div className="font-medium">Upload an image to enable 'Verified'</div>
                      {currentStatus === 'installed' && (
                        <button
                          onClick={() => {
                            setIsOpen(false);
                            onPhotoUpload?.(installation);
                          }}
                          className="text-orange-600 hover:text-orange-800 underline mt-1"
                        >
                          Upload Image
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Image Upload Button for Installed status without images */}
      {currentStatus === 'installed' && !hasPhotos && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPhotoUpload?.(installation)}
          className="ml-2 text-xs"
        >
          <Icon name="Camera" size={12} />
          Upload Image
        </Button>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                <Icon name="AlertTriangle" size={24} className="text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">Confirm Status Change</h3>
                <p className="text-sm text-muted-foreground">This action will update the installation status</p>
              </div>
            </div>
            
            <div className="mb-6">
              <p className="text-foreground mb-4">
                Change status from{' '}
                <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${getStatusConfig(currentStatus)?.chipColor}`}>
                  <Icon name={getStatusConfig(currentStatus)?.icon} size={10} />
                  {getStatusConfig(currentStatus)?.label}
                </span>
                {' '}to{' '}
                <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${getStatusConfig(pendingStatus)?.chipColor}`}>
                  <Icon name={getStatusConfig(pendingStatus)?.icon} size={10} />
                  {getStatusConfig(pendingStatus)?.label}
                </span>
                ?
              </p>
            </div>

            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button onClick={handleConfirm}>
                Confirm
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Undo Snackbar */}
      {showUndoSnackbar && (
        <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-foreground text-background px-4 py-3 rounded-lg shadow-lg z-50 flex items-center gap-3">
          <Icon name="CheckCircle" size={16} className="text-green-400" />
          <span className="text-sm">
            Status changed from {getStatusConfig(previousStatus)?.label} to {getStatusConfig(currentStatus)?.label}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleUndo}
            className="text-background hover:text-background/80 px-2 py-1 h-auto"
          >
            <Icon name="Undo2" size={14} />
            Undo
          </Button>
        </div>
      )}
    </>
  );
};

export default StatusDropdown;